import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    MenuItem,
    Button,
    Chip,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";
import { UploadCloud } from "lucide-react";
import "./DailySituationReport.css";

const backendUrl = (process.env.REACT_APP_BACKEND_API_URL_LOCAL || "").replace(/\/$/, "");
// Writes into statistics_acp (source='daily_report'), the same table the
// Data View tab reads - so a saved day shows up there under its year.
const LIST_URL = `${backendUrl}/common/acp-daily-list/`;
const SAVE_URL = `${backendUrl}/common/acp-daily-save/`;

// Column key -> header label. Same field set as statistics_acp /
// common ACP_ENTRY_FIELDS - keep in sync with dsrCsvTemplate.js.
const IMPACT_COLUMNS = [
    ["location", "Location"],
    ["wind_damage", "Wind Damage"],
    ["fire", "Fire"],
    ["flood", "Flood"],
    ["landslide", "Landslide"],
    ["storm_surge", "Storm Surge"],
    ["social_conflict", "Social Conflict"],
    ["other_hazard", "Other Hazard"],
    ["road_accident", "Road Accident"],
    ["dead", "Dead"],
    ["injured", "Injured"],
    ["missing", "Missing"],
    ["affected_families", "Affected Families"],
    ["total_family_members", "Total Family Members"],
    ["evacuated_people", "Evacuated People"],
    ["damaged_road", "Damaged Road"],
    ["bridges", "Bridges"],
    ["damaged_houses", "Damaged Houses"],
    ["public_buildings", "Public Buildings"],
    ["remarks", "Remarks"],
];

// Everything else is a count; these two are free text.
const TEXT_COLUMNS = new Set(["location", "remarks"]);

const todayISO = () => new Date().toISOString().slice(0, 10);

// A native date input only opens its calendar when you hit the small icon -
// this opens it on a click anywhere in the box instead. showPicker() needs
// a real browser (Chrome/Edge/recent Firefox); older/unsupported browsers
// just fall back to the input's normal click behavior.
const openDatePicker = (e) => {
    if (e.target.showPicker) {
        try { e.target.showPicker(); } catch { /* no-op: not focused/supported */ }
    }
};

const DailySituationReport = () => {
    const [date, setDate] = useState(todayISO());
    const [rows, setRows] = useState([]);
    const [dirtyKeys, setDirtyKeys] = useState(() => new Set());
    const [municipalityFilter, setMunicipalityFilter] = useState([]);
    const [subMunicipalityFilter, setSubMunicipalityFilter] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    // After a successful Save, narrow the grid down to just the filled-in
    // rows (dropping whatever nav filters were used to find them) so the
    // user can review exactly what's about to go to the DB. Touching any
    // filter again, switching date, or Reset all drop back to browsing
    // everything.
    const [showOnlyFilled, setShowOnlyFilled] = useState(false);

    // Keeps fetchRows (a stable useCallback) able to see the *current* dirty
    // set without re-creating itself every keystroke.
    const dirtyKeysRef = useRef(dirtyKeys);
    useEffect(() => {
        dirtyKeysRef.current = dirtyKeys;
    }, [dirtyKeys]);

    // Tracks which date is currently loaded, so a re-fetch of the SAME date
    // (e.g. Reset) can be told apart from switching to a different date.
    const loadedDateRef = useRef(null);

    // forceOverwrite=true (Reset) always replaces every row and clears dirty
    // state. Otherwise, unsaved (dirty) rows keep their local edits instead
    // of being wiped by the fresh server data - they only go away via Save
    // or Reset. Switching to a different date always loads clean.
    const fetchRows = useCallback(async (forDate, forceOverwrite = false) => {
        setLoading(true);
        try {
            const response = await fetch(`${LIST_URL}?date=${forDate}`);
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || "Failed to load the sheet");
            const isSameDate = !forceOverwrite && loadedDateRef.current === forDate;
            setRows((prevRows) => {
                const freshRows = data.rows || [];
                if (!isSameDate) return freshRows;
                const prevByCode = new Map(prevRows.map((r) => [r.adm3_code, r]));
                return freshRows.map((freshRow) => {
                    if (!dirtyKeysRef.current.has(freshRow.adm3_code)) return freshRow;
                    const prev = prevByCode.get(freshRow.adm3_code);
                    if (!prev) return freshRow;
                    const merged = { ...freshRow };
                    IMPACT_COLUMNS.forEach(([key]) => { merged[key] = prev[key]; });
                    return merged;
                });
            });
            if (!isSameDate) setDirtyKeys(new Set());
            loadedDateRef.current = forDate;
        } catch (error) {
            console.error("Error fetching daily situation report:", error);
            setSnackbar({ open: true, message: error.message || "Could not load the sheet.", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRows(date);
    }, [date, fetchRows]);

    const municipalities = useMemo(() => {
        const seen = new Map();
        rows.forEach((row) => seen.set(row.adm1_code, row.adm1));
        return Array.from(seen, ([adm1_code, adm1]) => ({ adm1_code, adm1 }));
    }, [rows]);

    const subMunicipalities = useMemo(() => {
        const seen = new Map();
        rows.forEach((row) => {
            if (municipalityFilter.length > 0 && !municipalityFilter.includes(row.adm1_code)) return;
            seen.set(row.adm2_code, row.adm2);
        });
        return Array.from(seen, ([adm2_code, adm2]) => ({ adm2_code, adm2 }));
    }, [rows, municipalityFilter]);

    // Narrowing Municipality can drop Administrative Posts that were
    // selected under a since-deselected municipality - keep only the ones
    // still valid instead of silently filtering against a stale selection.
    useEffect(() => {
        setSubMunicipalityFilter((prev) => {
            const validCodes = new Set(subMunicipalities.map((s) => s.adm2_code));
            const next = prev.filter((code) => validCodes.has(code));
            return next.length === prev.length ? prev : next;
        });
    }, [subMunicipalities]);

    // Any row with at least one filled field - Upload pushes ALL of these,
    // not just rows dirty since the last Save Changes click, since Save
    // Changes is a mid-way checkpoint and Upload is the day's final commit.
    const rowHasData = useCallback(
        (row) => IMPACT_COLUMNS.some(([key]) => row[key] !== null && row[key] !== undefined && row[key] !== ""),
        []
    );
    const filledCount = useMemo(() => rows.filter(rowHasData).length, [rows, rowHasData]);

    const visibleRows = useMemo(() => {
        return rows.filter((row) => {
            if (showOnlyFilled && !rowHasData(row)) return false;
            if (municipalityFilter.length > 0 && !municipalityFilter.includes(row.adm1_code)) return false;
            if (subMunicipalityFilter.length > 0 && !subMunicipalityFilter.includes(row.adm2_code)) return false;
            if (search && !row.adm3.toLowerCase().includes(search.toLowerCase())) return false;
            return true;
        });
    }, [rows, municipalityFilter, subMunicipalityFilter, search, showOnlyFilled, rowHasData]);

    const handleCellChange = (adm3_code, field, value) => {
        const parsedValue = TEXT_COLUMNS.has(field) ? (value === "" ? null : value) : (value === "" ? null : Number(value));
        setRows((prev) =>
            prev.map((row) => (row.adm3_code === adm3_code ? { ...row, [field]: parsedValue } : row))
        );
        setDirtyKeys((prev) => new Set(prev).add(adm3_code));
    };

    const handleSave = async () => {
        if (dirtyKeys.size === 0) return;
        setSaving(true);
        try {
            // Only adm3_code + the impact figures are persisted - names are
            // always resolved from the master Suco list, never stored here.
            // Only rows that are both dirty AND actually have a filled
            // field are sent - touching a cell and leaving it blank
            // shouldn't push an empty row to the DB.
            const dirtyRows = rows
                .filter((row) => dirtyKeys.has(row.adm3_code) && rowHasData(row))
                .map((row) => ({
                    adm3_code: row.adm3_code,
                    ...Object.fromEntries(IMPACT_COLUMNS.map(([key]) => [key, row[key]])),
                }));
            if (dirtyRows.length === 0) {
                setDirtyKeys(new Set());
                setSnackbar({ open: true, message: "No filled data among the changed rows - nothing saved.", severity: "info" });
                return;
            }
            const response = await fetch(SAVE_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ date, rows: dirtyRows }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || "Failed to save changes");
            setDirtyKeys(new Set());
            // Drop whatever nav filters were used to find these sucos, and
            // narrow the grid to just the filled-in rows - a clean review
            // of exactly what's saved, ready for Upload.
            setMunicipalityFilter([]);
            setSubMunicipalityFilter([]);
            setSearch("");
            setShowOnlyFilled(true);
            setSnackbar({ open: true, message: `Saved ${dirtyRows.length} row(s) for ${date}.`, severity: "success" });
        } catch (error) {
            console.error("Error saving daily situation report:", error);
            setSnackbar({ open: true, message: error.message || "Failed to save changes.", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    // Discards every unsaved edit and reloads the currently selected date
    // fresh from the server.
    const handleReset = () => {
        fetchRows(date, true);
        setShowOnlyFilled(false);
    };

    // Final commit for the day: push every filled row to the DB (a
    // separate append, even for rows already checkpointed via Save
    // Changes - statistics_acp is append-only, so this just records the
    // day's final values as their own row). The grid keeps showing what
    // was uploaded - normal display, still editable - rather than
    // clearing to blank.
    const handleUpload = async () => {
        const filledRows = rows.filter(rowHasData);
        if (filledRows.length === 0) return;
        setUploading(true);
        try {
            const payloadRows = filledRows.map((row) => ({
                adm3_code: row.adm3_code,
                ...Object.fromEntries(IMPACT_COLUMNS.map(([key]) => [key, row[key]])),
            }));
            const response = await fetch(SAVE_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ date, rows: payloadRows }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || "Failed to upload data");

            setDirtyKeys(new Set());
            setSnackbar({ open: true, message: `Uploaded ${payloadRows.length} row(s) for ${date}.`, severity: "success" });
        } catch (error) {
            console.error("Error uploading daily situation report:", error);
            setSnackbar({ open: true, message: error.message || "Failed to upload data.", severity: "error" });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box className="dsr-container">
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 2 }}>
                    <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: "18px" }}>
                        Daily Situation Report
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                        <Chip
                            label={`${rows.length} sucos loaded`}
                            sx={{ backgroundColor: "#2563eb18", color: "#2563eb", fontWeight: 700, border: "1px solid #2563eb30" }}
                        />
                        {showOnlyFilled && (
                            <Chip
                                label={`Showing filled only (${filledCount}) — click to show all`}
                                onClick={() => setShowOnlyFilled(false)}
                                sx={{
                                    backgroundColor: "#16a34a18", color: "#16a34a", fontWeight: 700,
                                    border: "1px solid #16a34a30", cursor: "pointer",
                                }}
                            />
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography sx={{ color: "#475569", fontSize: "13px", whiteSpace: "nowrap" }}>
                                {dirtyKeys.size > 0 ? `${dirtyKeys.size} unsaved row(s)` : "No unsaved changes"}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={handleReset}
                                disabled={dirtyKeys.size === 0 || saving || uploading}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderColor: "#e2e8f0",
                                    color: "#475569",
                                    "&:hover": { borderColor: "#cbd5e1", backgroundColor: "#f8fafc" },
                                }}
                            >
                                Reset
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={dirtyKeys.size === 0 || saving || uploading}
                                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                                sx={{
                                    backgroundColor: "#2563eb",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    px: 3,
                                    "&:hover": { backgroundColor: "#1d4ed8" },
                                    "&:disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8" },
                                }}
                            >
                                {saving ? "Saving..." : "Save Changes"}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleUpload}
                                disabled={filledCount === 0 || saving || uploading}
                                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadCloud size={16} />}
                                sx={{
                                    backgroundColor: "#16a34a",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    px: 3,
                                    "&:hover": { backgroundColor: "#15803d" },
                                    "&:disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8" },
                                }}
                            >
                                {uploading ? "Uploading..." : "Upload"}
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField
                        label="Date"
                        type="date"
                        size="small"
                        value={date}
                        onChange={(e) => { setDate(e.target.value); setShowOnlyFilled(false); }}
                        onClick={openDatePicker}
                        inputProps={{ onClick: openDatePicker }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 180 }}
                    />
                    <TextField
                        label="Filter by Municipality"
                        select
                        size="small"
                        value={municipalityFilter}
                        onChange={(e) => { setMunicipalityFilter(e.target.value); setShowOnlyFilled(false); }}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) =>
                                selected.length === 0
                                    ? "All Municipalities"
                                    : municipalities.filter((m) => selected.includes(m.adm1_code)).map((m) => m.adm1).join(", "),
                        }}
                        sx={{ minWidth: 240 }}
                    >
                        {municipalities.map((m) => (
                            <MenuItem key={m.adm1_code} value={m.adm1_code}>{m.adm1}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Filter by Sub-Municipality"
                        select
                        size="small"
                        value={subMunicipalityFilter}
                        onChange={(e) => { setSubMunicipalityFilter(e.target.value); setShowOnlyFilled(false); }}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) =>
                                selected.length === 0
                                    ? "All Sub-Municipalities"
                                    : subMunicipalities.filter((s) => selected.includes(s.adm2_code)).map((s) => s.adm2).join(", "),
                        }}
                        sx={{ minWidth: 260 }}
                    >
                        {subMunicipalities.map((s) => (
                            <MenuItem key={s.adm2_code} value={s.adm2_code}>{s.adm2}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Search Suco"
                        size="small"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setShowOnlyFilled(false); }}
                        sx={{ minWidth: 220 }}
                    />
                </Box>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <Box className="dsr-table-scroll">
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <table className="dsr-table">
                            <thead>
                                <tr>
                                    <th>No</th>
                                    <th>Municipality</th>
                                    <th>Administrative Post</th>
                                    <th>Suco</th>
                                    {IMPACT_COLUMNS.map(([key, label]) => (
                                        <th key={key}>{label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map((row, index) => (
                                    <tr key={row.adm3_code} className={dirtyKeys.has(row.adm3_code) ? "dsr-row-dirty" : ""}>
                                        <td>{index + 1}</td>
                                        <td>{row.adm1}</td>
                                        <td>{row.adm2}</td>
                                        <td>{row.adm3}</td>
                                        {IMPACT_COLUMNS.map(([key]) => (
                                            <td key={key} className="dsr-input-cell">
                                                <input
                                                    type={TEXT_COLUMNS.has(key) ? "text" : "number"}
                                                    min={TEXT_COLUMNS.has(key) ? undefined : "0"}
                                                    className="dsr-cell-input"
                                                    value={row[key] ?? ""}
                                                    onChange={(e) => handleCellChange(row.adm3_code, key, e.target.value)}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {visibleRows.length === 0 && (
                                    <tr>
                                        <td colSpan={4 + IMPACT_COLUMNS.length} className="dsr-empty">
                                            No sucos match the current filter.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ minWidth: "300px" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DailySituationReport;
